import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, deleteDoc, doc, writeBatch } from 'firebase/firestore';
import { db } from '../../firebase';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet';
import './NewStudentInquiry.css';

const BookInquiry = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch inquiries in real-time
  useEffect(() => {
    // Ask for notification permission
    if (Notification.permission !== 'granted') {
      Notification.requestPermission();
    }

    const unsubscribe = onSnapshot(
      collection(db, 'admissions'),
      (snapshot) => {
        const inquiryData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        // Sort the inquiries by timestamp to place new inquiries at the top
        inquiryData.sort((a, b) => b.timestamp.toDate() - a.timestamp.toDate());
        setInquiries(inquiryData);
        setLoading(false);

        // Push Notification when new inquiry is received
        if (Notification.permission === 'granted') {
          new Notification('New Admission Inquiry Received!', {
            body: 'A new inquiry has been submitted. Check your dashboard!',
          });
        }
      },
      (error) => {
        console.error('Error fetching inquiries:', error);
        toast.error('Failed to fetch inquiries');
      }
    );
    return () => unsubscribe(); // Cleanup listener on component unmount
  }, []);

  // Delete a single inquiry
  const deleteInquiry = async (id) => {
    try {
      const docRef = doc(db, 'admissions', id);
      await deleteDoc(docRef);
      setInquiries(inquiries.filter((inquiry) => inquiry.id !== id));
      toast.success('Inquiry deleted successfully!');
    } catch (error) {
      console.error('Error deleting inquiry:', error);
      toast.error('Error deleting inquiry');
    }
  };

  // Delete all inquiries after confirmation
  const deleteAllInquiries = async () => {
    const confirmDelete = window.confirm('Are you sure you want to delete all inquiries?');
    if (confirmDelete) {
      try {
        const batch = writeBatch(db);
        inquiries.forEach((inquiry) => {
          const docRef = doc(db, 'admissions', inquiry.id);
          batch.delete(docRef);
        });
        await batch.commit();
        setInquiries([]);
        toast.success('All inquiries deleted successfully!');
      } catch (error) {
        console.error('Error deleting all inquiries:', error);
        toast.error('Error deleting all inquiries');
      }
    }
  };

  // Export inquiries to Excel
  const exportToExcel = () => {
    // Convert the inquiries data into an array of objects
    const inquiriesData = inquiries.map((inquiry) => ({
      Name: inquiry.name,
      Course: inquiry.course,
      Branch: inquiry.branch,
      'Father\'s Name': inquiry.fathersName,
      'Father\'s Mobile': inquiry.fathersMobile,
      Email: inquiry.email,
      Phone: inquiry.mobile,
      Address: inquiry.address,
      'Aadhar Card': inquiry.adharCard,
      '10th Marksheet': inquiry.marksheet10,
      '12th Marksheet': inquiry.marksheet12,
      Timestamp: inquiry.timestamp ? inquiry.timestamp.toDate().toLocaleString() : 'N/A',
    }));

    // Create a new worksheet from the data
    const ws = XLSX.utils.json_to_sheet(inquiriesData);

    // Create a new workbook
    const wb = XLSX.utils.book_new();

    // Append the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Inquiries');

    // Export the workbook as an Excel file
    XLSX.writeFile(wb, 'Inquiries.xlsx');
  };

  return (
    <div className="newStudentInquiry">
      <Helmet>
        <meta charSet="utf-8" />
        <title>New Student Inquiries</title>
        <meta name="description" content="Manage all student inquiries and submissions." />
      </Helmet>

      <h1 className="dashboardTitle">New Student Inquiries</h1>

      {/* Buttons to export data and delete inquiries */}
      <div className="actionButtons">
        <button className="exportBtn" onClick={exportToExcel}>
          Download All as Excel
        </button>
        <button className="deleteAllBtn" onClick={deleteAllInquiries}>
          Delete All Inquiries
        </button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <table className="inquiryTable">
          <thead>
            <tr>
              <th>Name</th>
              <th>Course</th>
              <th>Branch</th>
              <th>Father's Name</th>
              <th>Father's Mobile</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Address</th>
              <th>Aadhar Card</th>
              <th>10th Marksheet</th>
              <th>12th Marksheet</th>
              <th>Timestamp</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {inquiries.map((inquiry) => (
              <tr key={inquiry.id}>
                <td>{inquiry.name}</td>
                <td>{inquiry.course}</td>
                <td>{inquiry.branch}</td>
                <td>{inquiry.fathersName}</td>
                <td>{inquiry.fathersMobile}</td>
                <td>{inquiry.email}</td>
                <td>{inquiry.mobile}</td>
                <td>{inquiry.address}</td>

                {/* Display Aadhar Card Image */}
                <td>
                  {inquiry.adharCard && (
                    <img src={inquiry.adharCard} alt="Aadhar Card" style={{ width: '100px', height: 'auto' }} />
                  )}
                </td>

                {/* Display 10th Marksheet Image */}
                <td>
                  {inquiry.marksheet10 && (
                    <img src={inquiry.marksheet10} alt="10th Marksheet" style={{ width: '100px', height: 'auto' }} />
                  )}
                </td>

                {/* Display 12th Marksheet Image */}
                <td>
                  {inquiry.marksheet12 && (
                    <img src={inquiry.marksheet12} alt="12th Marksheet" style={{ width: '100px', height: 'auto' }} />
                  )}
                </td>

                <td>
                  {inquiry.timestamp ? inquiry.timestamp.toDate().toLocaleString() : 'N/A'}
                </td>
                <td>
                  <button
                    className="deleteBtn"
                    onClick={() => deleteInquiry(inquiry.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default BookInquiry;
